import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import infoIcon from '@/assets/icons/info.svg';

// services
import { expenseRevenueService } from '@/services/expense-category.service';

// components
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import InfoModal from '@/components/molecules/InfoModal';
import ECGeneralInfoAccordion from '@/components/molecules/ECGeneralInfoAccordion';
import MobileAppBar from '@/components/molecules/MobileAppBar';

// icons
import expenseIcon from '@/assets/icons/expense-2.svg';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import binIcon from '@/assets/icons/bin.svg';
import editIcon from '@/assets/icons/edit.svg';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import deleteBankAccountIcon from '@/assets/icons/delete-bankAccount.svg';

import ecExpanatoryImg1 from '@/assets/images/ec-explanatory-1.png';
import ecExpanatoryImg2 from '@/assets/images/ec-explanatory-2.png';
import ecExpanatoryImg3 from '@/assets/images/ec-explanatory-3.png';

import ExplanatoryModal from '@/components/molecules/ExplanatoryModal';
import { Invoice } from '@/services/types/invoice.types';
import { withPaymentAmountFromAllocationKey } from '@/utils/invoiceAllocationAmount';
import { invoiceService } from '@/services/invoice.service';
import { PAGE } from '@/routes';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';
import TabPills from '@/components/molecules/TabPills';
import InvoicesTable from '@/components/molecules/invoice/InvoicesTable';
import { InvoiceCategory } from '@/services/types/enums/invoice.enum';

const steps = [
  {
    title: 'What Are Expense Categories?',
    message:
      'Simplify your expense tracking with Expense Categories. Allocate costs to understand where your money is going.',
    img: ecExpanatoryImg1
  },
  {
    title: 'Setting Expense Limits (ECMEV)',
    message:
      'Each Expense Category has a defined Expense Category Maximum Expense Value (ECMEV) to control spending effectively.',
    img: ecExpanatoryImg2
  },
  {
    title: 'Linking Invoices Expense Categories',
    message:
      'Assign expense-related invoices to the relevant Expense Category to maintain a detailed financial overview.',
    img: ecExpanatoryImg3
  }
];

const ECDetailPage = () => {
  const [expenseCategory, setExpenseCategory] = useState<any>(null);
  const [graphViewMode, setGraphViewMode] = useState('percentage');
  const { ecId, budgetId } = useParams();
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [activeInvoiceTab, setActiveInvoiceTab] = useState('All');

  const navigate = useNavigate();
  const location = useLocation();

  const ecData = location.state?.ecData as any | undefined;
  const budget = location.state?.budget as any | undefined;

  const { can } = useBudgetPermissions({
    budgetId,
    fallbackMembers: budget?.members ?? []
  });
  const canCreateInvoice = can('invoice:create');
  const canEditExpenseCategory = can('expenseCategory:edit');
  const canDeleteExpenseCategory = can('expenseCategory:delete') && !expenseCategory?.is_default;

  const handleInvoiceSelect = (invoice: Invoice & { budget?: any }) => {
    if (budgetId) {
      navigate(`${PAGE.BUDGETS.path}/${budgetId}/invoices/${invoice._id}`, {
        state: { budget, from: 'expenseCategory', ecData }
      });
    }
  };

  console.log('budgetId', budgetId);
  console.log('ecId', ecId);

  const fetchExpenseCategory = async () => {
    try {
      const { data: ecData } = await expenseRevenueService.getExpenseCategory(budgetId || '', ecId || '');
      setExpenseCategory(ecData);
    } catch (error) {
      console.error('Error fetching expense category:', error);
    }
  };

  const fetchExpenseCategoryInvoices = async () => {
    if (ecId) {
      try {
        const { data: invoicesData } = await invoiceService.getInvoices({
          page: 1,
          limit: 12,
          expense_category_id: ecId,
          invoice_category: activeInvoiceTab === 'All' ? undefined : activeInvoiceTab.toUpperCase(),
          type: undefined
        });
        const filteredInvoices =
          invoicesData.data && invoicesData.data.length > 0
            ? invoicesData.data
                .filter(
                  (invoice: Invoice) =>
                    invoice.is_draft === false && invoice.invoice_category === InvoiceCategory.EXPENSE
                )
                .map((invoice: Invoice) =>
                  withPaymentAmountFromAllocationKey(invoice, ecId, 'allocation_expense_category')
                )
            : [];
        setInvoices(filteredInvoices);
      } catch (error) {
        console.error('Error fetching ec invoices:', error);
      } finally {
        setLoadingInvoices(false);
      }
    }
  };

  useEffect(() => {
    fetchExpenseCategory();
  }, []);

  useEffect(() => {
    fetchExpenseCategoryInvoices();
  }, [activeInvoiceTab, ecId]);

  const handleDeleteExpenseCategory = async () => {
    try {
      await expenseRevenueService.deleteExpenseCategory(budgetId || '', ecId || '');
      toast.success('Expense category deleted successfully');
      navigate(-1);
    } catch (error) {
      console.error('Error deleting expense category:', error);
    }
  };

  // Calculate chart data for ECMEV
  const ecmevTotal = expenseCategory?.ecmev + expenseCategory?.ecmev_used || 0;
  const ecmevUsed = expenseCategory?.ecmev_used || 0;
  const ecmevRemaining = expenseCategory?.remaining_ecmev || 0;

  const ecmevChartData = [
    {
      key: 'remaining',
      value: ecmevTotal === 0 ? 100 : Math.round((ecmevRemaining / ecmevTotal) * 100),
      fill: '#32ADE6'
    },
    {
      key: 'spent',
      value: ecmevTotal === 0 ? 0 : Math.round((ecmevUsed / ecmevTotal) * 100),
      fill: '#FF2D55'
    }
  ];

  const ecmevChartConfig = {
    remaining: { label: 'Remaining Budget', color: '#32ADE6' },
    spent: { label: 'Budget Spent', color: '#FF2D55' }
  };

  return (
    <>
      <MobileAppBar
        title="General Expense Category"
        onRightIconClick={() => {
          setCurrentStep(0);
          setIsOpen(true);
        }}
        rightIcon={infoIcon}
        rightTwoIcon={canDeleteExpenseCategory ? binIcon : undefined}
        onRightTwoIconClick={canDeleteExpenseCategory ? () => setDeleteConfirmModal(true) : undefined}
      />

      <div className="flex-1 flex flex-col gap-8 md:gap-4 p-4 md:p-0">
        <div className="flex flex-col-reverse md:flex-col lg:flex-row gap-8 lg:gap-4">
          <div className="hidden md:border md:flex flex-col flex-1 p-4 md:bg-white md:rounded-[16px]">
            {/* Web View Page Header */}
            <header className="hidden md:block">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src={expenseIcon} alt="expense-icon" width={40} height={40} />
                </div>

                <div className="flex items-center gap-3">
                  {canEditExpenseCategory && (
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/budgets/${budgetId}/expense-categories/edit/${ecId}`, {
                          state: { ecData }
                        });
                      }}
                      title="Edit"
                    >
                      <img src={editIcon} alt="edit-icon" width={28} height={28} />
                    </button>
                  )}
                  <button
                    title="Info"
                    onClick={() => {
                      setCurrentStep(0);
                      setIsOpen(true);
                    }}
                  >
                    <img className="w-[28px] h-[28px]" src={infoIcon} alt="info icon" />
                  </button>
                  {canDeleteExpenseCategory && (
                    <button type="button" onClick={() => setDeleteConfirmModal(true)} title="Delete">
                      <img src={binIcon} alt="bin-icon" width={28} height={28} />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-[24px] font-semibold mt-2">{expenseCategory?.name}</p>
            </header>

            {/* Web View General Information */}
            <div className="hidden md:block border border-primary rounded-[16px] p-4 bg-lightPurple flex-1 space-y-3 mt-3">
              <p className="text-primary font-bold text-base mb-3">General Information</p>

              <div>
                <p className="text-sm text-secondary font-medium leading-[18px]">Description</p>
                <p className="text-base font-medium leading-[24.5px]">
                  {expenseCategory?.description || 'No description provided'}
                </p>
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <p className="text-sm text-secondary font-medium leading-[18px]">ECMEV</p>
                  <p className="text-base font-medium leading-[24.5px]">
                    €{expenseCategory?.ecmev?.toLocaleString()} (
                    {((expenseCategory?.ecmev / (expenseCategory?.bmev || 0)) * 100)?.toFixed(0)}% of BMEV)
                  </p>
                </div>

                <div className="flex-1">
                  <p className="text-sm text-secondary font-medium leading-[18px]">ECMEV Alerting Threshold</p>
                  <p className="text-base font-medium leading-[24.5px]">{expenseCategory?.ecmev_alert_threshold}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile View General Information */}
          <div className="md:hidden">
            <ECGeneralInfoAccordion ecData={expenseCategory || {}} />
          </div>

          {/* GRAPH OVERVIEW */}
          <div className="flex flex-col lg:w-[40%] md:bg-white md:p-[0.85rem] md:rounded-[16px] md:border">
            <header className="hidden w-full md:flex items-center justify-between mb-3 md:mb-4">
              <h3 className="text-base font-bold text-primary md:text-foreground">Graph Overview</h3>
              <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
                <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                  <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                    <img
                      src={percentageIcon}
                      alt="percentage-icon"
                      width={13}
                      height={17}
                      className="group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                  <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                    <img
                      src={euroIcon}
                      alt="euro-icon"
                      width={12}
                      height={12}
                      className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </header>

            {/* Graph  */}
            <div className=" flex-1 flex items-center justify-between gap-4 md:border bg-white rounded-[16px] p-4 px-5">
              <div className="flex flex-col gap-1">
                <Tabs
                  className="md:hidden mb-3"
                  defaultValue="percentage"
                  value={graphViewMode}
                  onValueChange={setGraphViewMode}
                >
                  <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                    <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                      <img
                        src={percentageIcon}
                        alt="percentage-icon"
                        width={13}
                        height={17}
                        className="group-data-[state=active]:brightness-[100]"
                      />
                    </TabsTrigger>
                    <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                      <img
                        src={euroIcon}
                        alt="euro-icon"
                        width={12}
                        height={12}
                        className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                      />
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <p className="text-sm text-secondary">Remaining ECMEV</p>
                <p className="text-[28px] font-bold leading-[24px] mb-2 md:mb-0">
                  {graphViewMode === 'percentage'
                    ? `${ecmevChartData[0].value}%`
                    : `€${ecmevRemaining.toLocaleString()}`}
                </p>
                <div className="mt-2 md:mt-8">
                  <div className="flex items-center gap-2">
                    <span className="w-[9px] h-[9px] rounded-full bg-[#32ADE6]"></span>
                    <p className="text-sm text-secondary">Remaining Budget</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-[9px] h-[9px] rounded-full bg-[#FF2D55]"></span>
                    <p className="text-sm text-secondary">Budget Spent</p>
                  </div>
                </div>
              </div>
              <div>
                <PieGraph
                  title="Remaining ECMEV"
                  titleValue={
                    graphViewMode === 'percentage'
                      ? `${ecmevChartData[0].value}%`
                      : `€${ecmevRemaining.toLocaleString()}`
                  }
                  chartConfig={ecmevChartConfig}
                  chartData={ecmevChartData}
                  className=""
                  outerRadius={60}
                  minHeight={120}
                  hideLabel={true}
                />
              </div>
            </div>
          </div>

          {/* Mobile View Page Header */}
          <header className="flex items-center justify-between gap-4 mb-0 md:hidden">
            <p className="flex items-center gap-3">
              <img src={expenseIcon} alt="expense-icon" width={40} height={40} />
              <span className="text-base font-semibold">{expenseCategory?.name || 'Expense Category'}</span>
            </p>

            {canEditExpenseCategory && (
              <button
                type="button"
                onClick={() => {
                  navigate(`/budgets/${budgetId}/expense-categories/edit/${ecId}`, {
                    state: { ecData }
                  });
                }}
                title="Edit"
              >
                <img src={editIcon} alt="edit-icon" width={28} height={28} />
              </button>
            )}
          </header>
        </div>

        {/* Web View Recent Expenses */}
        <div className="hidden flex-1 md:flex flex-col border bg-white rounded-[16px] p-4">
          <header>
            <h4 className="flex items-center gap-2">
              <img src={invoiceIcon} alt="invoice-icon" />
              <span className="font-bold">Recent Expenses</span>
            </h4>
          </header>
          {invoices.length > 0 || activeInvoiceTab !== 'All' ? (
            <div className="flex flex-col gap-2 bg-lightPurple rounded-[16px] p-4 pt-2 mt-4 md:mt-3 md:p-0 md:bg-white">
              {/* <TabPills
                options={['All', 'Expense', 'Revenue']}
                value={activeInvoiceTab}
                onValueChange={setActiveInvoiceTab}
              /> */}

              <InvoicesTable
                invoices={invoices.slice(0, 10)}
                setSelectedInvoice={() => {}}
                onInvoiceSelect={handleInvoiceSelect}
              />
            </div>
          ) : (
            <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
              <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                <img src={noInvoiceIcon} alt="no invoice icon" className="md:w-[40px] md:h-[40px]" />
                <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                  <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                    There are no Invoice to show.
                  </span>{' '}
                  <span className="md:text-secondary md:text-base md:font-medium">
                    {' '}
                    Please create a new invoice to allocate budget!
                  </span>
                </span>
              </div>
              {canCreateInvoice && (
                <Button
                  onClick={() =>
                    navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
                      state: { from: 'budget', budgetId }
                    })
                  }
                  variant="primary-reverse"
                  className="rounded-full font-semibold px-5 text-base"
                >
                  Create an Invoice
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Recent Expenses (Mobile) */}
        <div className="flex-1 pb-8 md:hidden flex-col">
          <header className="mb-4">
            <h4 className="flex items-center gap-2">
              <span className="font-bold">Invoice</span>
            </h4>
          </header>
          <div className="flex-1 bg-lightPurple rounded-[16px] py-[1.5rem] px-[1rem] flex flex-col items-center justify-center gap-[1rem]">
            <img className="w-[80px] h-[80px]" src={noInvoiceIcon} alt="no-expense-icon" />
            <p className="text-center font-medium text-xs w-[80%]">
              There are no Invoice to show. Please create a new invoice to allocate budget!
            </p>

            {canCreateInvoice && (
              <Button
                onClick={() =>
                  navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
                    state: { from: 'budget', budgetId }
                  })
                }
                variant={'primary-reverse'}
                className="rounded-full px-6 w-fit text-base font-semibold"
              >
                Create an Invoice
              </Button>
            )}
          </div>
        </div>

        {/* Delete Confirm Modal */}
        {deleteConfirmModal && (
          <InfoModal
            isOpen={deleteConfirmModal}
            variant="confirm"
            title="Are you sure want to Delete this Expense Category?"
            message={<p>Deleted Expense Categories will be removed permanently and unable to restore</p>}
            buttonText="Delete"
            onButtonClick={handleDeleteExpenseCategory}
            onClose={() => setDeleteConfirmModal(false)}
            icon={deleteBankAccountIcon}
          />
        )}

        {/* Explanatory Modal */}
        {isOpen && (
          <ExplanatoryModal
            isOpen={isOpen}
            steps={steps}
            currentStep={currentStep}
            buttonText="Next"
            onButtonClick={() => {
              setCurrentStep(currentStep + 1);
            }}
            onClose={() => {
              setIsOpen(false);
            }}
            onStepChange={setCurrentStep}
          />
        )}
      </div>
    </>
  );
};

export default ECDetailPage;
